/**
 * Dark-colour heuristic (Phase 65 / BLU-594 PR2, frontend-only).
 *
 * There is no shared DARK_COLOUR_PATTERNS list, so this is a small pure matcher
 * used by the "Auto-detect dark colours" affordance and nowhere authoritative:
 * it only SEEDS the operator's `darkColours` buffer, which they then review and
 * adjust by hand before saving. Deliberately conservative — a false negative
 * (operator adds it manually) is cheaper than a false positive (wrong print set
 * silently shipped).
 */

/**
 * Substrings that reliably read as "dark garment". Word-ish tokens are matched
 * whole-word where sensible; broad families (charcoal, forest) match as
 * substrings so "Forest Green" / "Charcoal Marle" still resolve.
 */
const DARK_COLOUR_PATTERNS: readonly RegExp[] = [
  /\bblack\b/,
  /\bnavy\b/,
  /\bcharcoal\b/,
  /\bforest\b/,
  /\bmaroon\b/,
  /\bmidnight\b/,
  /\bindigo\b/,
  /\bmarine\b/,
  /\bebony\b/,
  /\bonyx\b/,
  /\bslate\b/,
  /\bgraphite\b/,
  /\bbottle\b/,
  /\bburgundy\b/,
  /\boxblood\b/,
  /\bespresso\b/,
  /\bplum\b/,
  /\baubergine\b/,
  /\bteal\b/,
  /\bpine\b/,
  // "Dark <anything>" — dark grey, dark green, dark heather, etc.
  /\bdark\b/,
];

/** True when a colour NAME reads as a dark garment by the conservative heuristic. */
export function isLikelyDarkColour(name: string): boolean {
  const n = name.trim().toLowerCase();
  if (!n) return false;
  return DARK_COLOUR_PATTERNS.some((re) => re.test(n));
}

/**
 * Filter a list of colour names to those that read as dark, deduping
 * case-insensitively while preserving the first-seen spelling.
 */
export function detectDarkColours(names: readonly string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const name of names) {
    if (!isLikelyDarkColour(name)) continue;
    const key = name.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(name.trim());
  }
  return out;
}
